// Shared by the Radix layers that have to hear an `Escape` without consuming
// it: the shared menu while its paper leaves (`icon-menu-button.tsx`), and
// `MuiTooltip`. Its own module because a component file that exports a
// non-component breaks fast refresh (see `icon-menu-context.ts`).

/**
 * Lets one `Escape` past a Radix dismissable layer **unconsumed**, while the
 * layer still runs its own dismissal.
 *
 * Radix's layer calls its `onEscapeKeyDown`, then, unless the key is already
 * defaulted, `preventDefault()`s it and dismisses
 * (`react-dismissable-layer/dist/index.mjs:59-67`). No prop keeps the layer from
 * consuming the key short of unmounting it. So, called from `onEscapeKeyDown`,
 * this answers the `preventDefault` that follows with an own-property
 * stand-in. The layer dismisses as usual, and the event goes on to the page as
 * the browser delivered it, where whatever answers an undefaulted `Escape`
 * next can hear it (`AppDialog`'s handler, for one).
 *
 * The stand-in answers every call for the rest of Radix's listener, not just
 * the first. Radix makes its call in the same synchronous turn as this
 * callback, and a microtask, which runs once that listener returns, takes the
 * stand-in away. So it never outlives the listener, whether a later Radix makes
 * one call, several, or none. Only on an undefaulted key: on a defaulted one
 * Radix makes no call, and dismisses nothing either.
 */
export function spareFromRadixDismiss(event: KeyboardEvent) {
  if (event.defaultPrevented) return;
  Object.defineProperty(event, "preventDefault", { configurable: true, value: () => {} });
  queueMicrotask(() => Reflect.deleteProperty(event, "preventDefault"));
}
